import { whenTransitionEnds } from './transition-util'

export const capitalize = (word: string): string => {
  const firstSymbol = word.slice(0, 1)
  const restWordPart = word.slice(1)

  return firstSymbol.toUpperCase() + restWordPart
}

export const delay = (delay: number = 0): Promise<void> => {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, delay)
  })
}

export const viewportObserver = new IntersectionObserver((entries, _) => {
  entries.forEach(entry => {
    const isInViewport = entry.isIntersecting
    const isParentHidden = entry.target.closest('[aria-hidden="true"], .hidden')

    entry.target.classList.toggle(
      'is-in-viewport',
      isInViewport && !isParentHidden,
    )
  })
})

export const getFocusTargets = (): string => {
  const targets = [
    'button:enabled',
    'input:not([type=hidden]):enabled',
    'textarea:enabled',
    'a[href]',
    '[data-focus-trap-target]',
    '[tabindex]:not([tabindex^="-"])',
  ]

  return targets
    .map(
      target =>
        `${target}:not([data-color-swatches-hidden-count]:empty):not(.hidden)`,
    )
    .join(',')
}

export const getTargets = element => {
  const targets = $list(getFocusTargets(), element).filter(
    target => target.clientWidth > 0,
  )
  const firstTarget = targets[0]
  const lastTarget = targets[targets.length - 1]

  return { targets, firstTarget, lastTarget }
}

export const appendElementToBody = customElement => {
  const customElementContainer = document.createElement('div')

  customElementContainer.classList.add('hidden')
  document.body.appendChild(customElementContainer)
  customElementContainer.appendChild(customElement)

  return customElementContainer
}

export const removeCustomElementFromBody = customElementContainer => {
  customElementContainer.remove()
}

export const isSafari = () => {
  // Chrome DevTools does not complain about navigator.vendor
  return /apple/i.test(navigator.vendor)
}

export const createPortal = (children, parent) => {
  let element = $el('#Portal')

  element.appendChild(children)

  const target = parent && parent.appendChild ? parent : document.body

  target.appendChild(element)
}

export const isExternalLink = (href: string): boolean => {
  return !href.match(/^\//) && !href.includes(window.location.host)
}

export const isCurrentPageLink = (href: string): boolean => {
  return href === window.location.href
}

export const createPrefetchLink = (href: string) => {
  const link = document.createElement('link')
  link.setAttribute('rel', 'prefetch')
  link.setAttribute('href', href)
  document.head.appendChild(link)
}

export const upperCamelCaseToSnakeCase = (value: string): string => {
  return value
    .replace(/^([A-Z])/, $1 => $1.toLowerCase())
    .replace(/([A-Z])/g, $1 => '-' + $1.toLowerCase())
}

export const registerComponent = (componentClass): void => {
  try {
    const name = upperCamelCaseToSnakeCase(componentClass.name)
    customElements.define(name, componentClass)
  } catch (error) {
    console.log(`component has not been defined`)
  }
}

export const whenDefined = (
  componentTag: string,
): Promise<CustomElementConstructor> => {
  return customElements.whenDefined(componentTag)
}

export const transitionToPromise = el => {
  return new Promise(resolve => {
    whenTransitionEnds(el, () => {
      resolve(null)
    })
  })
}

export const $el = <T extends HTMLElement>(
  selector,
  scope: any = document,
): T | null => {
  return scope.querySelector(selector)
}

export const $list = <T extends HTMLElement>(
  selector,
  scope: any = document,
): T[] => {
  return [...scope.querySelectorAll(selector)]
}

export const $elParent = <T extends HTMLElement>(
  selector,
  scope: any = document,
): T | null => {
  return scope.closest(selector)
}

export const isNotIframe = () => {
  return window.self === window.top
}

export const isNotThemeStore = () => {
  return window.self === window.top || Shopify.designMode
}
